import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { NodeType } from '@/types'

export type Surface = 'pulse' | 'navigator' | 'graph' | 'canvas' | 'focus'

export interface NavigationEntry {
  surface: Surface
  params?: Record<string, string>
  timestamp: number
  title: string
}

export interface Breadcrumb {
  id: string
  label: string
  type: 'surface' | 'item' | 'filter'
  path?: string
}

export interface PreviewableItem {
  id: string
  type: NodeType
  name: string
  content?: string
  domain?: {
    id: string
    name: string
    color: string
  }
  metadata?: Record<string, unknown>
}

interface NavigationState {
  // Navigation history
  history: NavigationEntry[]
  currentIndex: number

  // Contextual breadcrumb
  breadcrumbs: Breadcrumb[]

  // Item in preview (side panel)
  previewItem: PreviewableItem | null

  // Actions
  navigate: (entry: NavigationEntry) => void
  goBack: () => void
  goForward: () => void
  canGoBack: () => boolean
  canGoForward: () => boolean
  setPreview: (item: PreviewableItem | null) => void
  addBreadcrumb: (crumb: Breadcrumb) => void
  setBreadcrumbs: (crumbs: Breadcrumb[]) => void
  clearBreadcrumbs: () => void
  getCurrentEntry: () => NavigationEntry | null
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      history: [],
      currentIndex: -1,
      breadcrumbs: [],
      previewItem: null,

      navigate: (entry) => {
        const { history, currentIndex } = get()
        // Remove forward history when navigating to new page
        const newHistory = [...history.slice(0, currentIndex + 1), entry]
        // Keep only last 50 entries
        const trimmedHistory = newHistory.slice(-50)
        set({
          history: trimmedHistory,
          currentIndex: trimmedHistory.length - 1
        })
      },

      goBack: () => {
        const { currentIndex } = get()
        if (currentIndex > 0) {
          set({ currentIndex: currentIndex - 1 })
        }
      },

      goForward: () => {
        const { history, currentIndex } = get()
        if (currentIndex < history.length - 1) {
          set({ currentIndex: currentIndex + 1 })
        }
      },

      canGoBack: () => {
        const { currentIndex } = get()
        return currentIndex > 0
      },

      canGoForward: () => {
        const { history, currentIndex } = get()
        return currentIndex < history.length - 1
      },

      setPreview: (item) => set({ previewItem: item }),

      addBreadcrumb: (crumb) => {
        const { breadcrumbs } = get()
        // Remove duplicates and keep last 5
        const filtered = breadcrumbs.filter(b => b.id !== crumb.id)
        set({ breadcrumbs: [...filtered, crumb].slice(-5) })
      },

      setBreadcrumbs: (crumbs) => set({ breadcrumbs: crumbs }),

      clearBreadcrumbs: () => set({ breadcrumbs: [] }),

      getCurrentEntry: () => {
        const { history, currentIndex } = get()
        return currentIndex >= 0 ? history[currentIndex] : null
      }
    }),
    {
      name: 'rsb-navigation',
      partialize: (state) => ({
        history: state.history.slice(-20), // Only persist last 20 entries
        currentIndex: Math.min(state.currentIndex, 19)
      })
    }
  )
)
